import { useEffect, useId, useRef, type ReactNode } from 'react';
import './ui.css';

/** What the confirming button looks like: Wake is ember, Sleep moonlight, anything destructive red. */
export type Tone = 'ember' | 'moon' | 'danger';

const TONE_CLASS: Record<Tone, string> = { ember: 'btn btn-primary', moon: 'btn btn-moon', danger: 'btn btn-danger' };

/**
 * Asks before doing something (waking a server, putting it to sleep, removing a friend). A native
 * modal `<dialog>`: focus stays inside, Escape and Cancel close it, the page behind is inert.
 * While `busy` (the request is on its way), it can't be closed or confirmed twice.
 */
export function ConfirmDialog(props: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  tone: Tone;
  busy?: boolean;
  /** The confirming button stays off until this is false (e.g. a name typed to confirm a destroy). */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { open, title, children, confirmLabel, tone, busy = false, confirmDisabled = false, onConfirm, onCancel } = props;
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault(); // Escape: closed through onCancel, so `open` stays the truth
        if (!busy) onCancel();
      }}
    >
      {open && (
        <div className="dialog-body">
          <h2 id={titleId} className="dialog-title">{title}</h2>
          <div className="dialog-text">{children}</div>
          <div className="dialog-actions">
            {/* Focus starts on Cancel: a stray Enter never wakes or stops anything. */}
            <button className="btn" type="button" onClick={onCancel} disabled={busy} autoFocus>Cancel</button>
            <button className={TONE_CLASS[tone]} type="button" onClick={onConfirm} disabled={busy || confirmDisabled}>
              {busy ? `${confirmLabel}…` : confirmLabel}
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
}
